<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { UserUpdateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import {
  NotificationItem,
  reviseNotifications,
} from '@/utils/notification-utils';

// --- Props for user details ---
const props = defineProps<{
  notifications: NotificationItem[];
  loading: boolean;
}>();

// --- Local state for user details ---
const userNotifications = ref<NotificationItem[]>([]);

// Watch the incoming user details for updates => update local form
watch(
  () => props.notifications,
  (newVal) => {
    console.debug(
      'User notifications updated: outside of the UserNotifications component',
      newVal
    );
    userNotifications.value = reviseNotifications(props.notifications);
  },
  { immediate: true }
);

// --- Emit "userUpdated" when personal details are updated ---
const emits = defineEmits(['userUpdated']);

// Loading and Error States
const mutationInProgress = ref(false);
const showUpdateSuccess = ref(false);
const mutationError = ref<string | null>(null);

// --- Mutation to updated user`s personal details ---
const {
  mutate: updateDetails,
  onDone: onUpdateDone,
  onError: onUpdateError,
} = useMutation(UserUpdateDocument);

// Handle the result of the mutation
onUpdateDone((res) => {
  if (res.data && res.data.userUpdate) {
    console.debug('User notifications updated:', res.data.userUpdate);
    emits('userUpdated');
    // Show success indicator and hider after 2 seconds
    mutationInProgress.value = false;
    showUpdateSuccess.value = true;
    setTimeout(() => {
      showUpdateSuccess.value = false;
    }, 1000);
  } else {
    console.error('User update failed:', res.data);
  }
});

// Handle errors in the mutation
onUpdateError((err) => {
  console.error('Error updating user notification settings:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// Submit form with personal details
async function onSubmit() {
  // Reset error and submit the update request
  mutationError.value = null;
  mutationInProgress.value = true;

  updateDetails({
    notifications: userNotifications.value,
  });
}
</script>

<template>
  <div>
    <b-form @submit.prevent="onSubmit">
      <template
        v-for="notification in userNotifications"
        :key="notification.name"
      >
        <BFormCheckbox v-model="notification.enabled">
          {{ notification.label }}
          <font-awesome-icon
            :id="notification.name"
            icon="question-circle"
            class="me-2 text-secondary"
          />
        </BFormCheckbox>
        <BTooltip
          :target="notification.name"
          :title="notification.description"
        />
      </template>

      <!-- Save Changes Button -->
      <b-form-group class="mt-3">
        <b-button
          type="submit"
          variant="outline-dark"
          :disabled="mutationInProgress"
        >
          <span v-if="mutationInProgress">
            Saving Changes
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="showUpdateSuccess">
            Changes Saved
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Save Changes </span>
        </b-button>
      </b-form-group>

      <!-- Error Message -->
      <b-alert
        v-model="mutationError"
        variant="danger"
        dismissible
        class="mt-3"
        @dismissed="mutationError = null"
      >
        {{ mutationError }}
      </b-alert>
    </b-form>
  </div>
</template>
<style scoped></style>
